import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-sm font-medium ring-offset-background transition-all duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: [
          "bg-primary text-primary-foreground",
          "shadow-[0_4px_16px_hsl(var(--glass-shadow)),inset_0_1px_0_hsl(0_0%_100%/0.15)]",
          "hover:shadow-[0_8px_24px_hsl(var(--glass-shadow-strong)),inset_0_1px_0_hsl(0_0%_100%/0.2)]",
          "hover:scale-[1.02] active:scale-[0.98]",
          "backdrop-blur-sm"
        ].join(" "),
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-card backdrop-blur-sm",
        outline: [
          "border border-[hsl(var(--glass-border-strong))]",
          "bg-[hsl(var(--glass-bg))] backdrop-blur-lg",
          "shadow-[inset_0_1px_0_hsl(var(--glass-highlight))]",
          "hover:bg-[hsl(var(--glass-bg-strong))]",
          "hover:border-[hsl(var(--glass-border))]",
          "hover:shadow-[0_4px_16px_hsl(var(--glass-shadow)),inset_0_1px_0_hsl(var(--glass-highlight))]"
        ].join(" "),
        secondary: [
          "bg-[hsl(var(--glass-bg-strong))] text-secondary-foreground",
          "backdrop-blur-lg",
          "shadow-[inset_0_1px_0_hsl(var(--glass-highlight))]",
          "hover:bg-[hsl(var(--glass-bg))]"
        ].join(" "),
        ghost: "hover:bg-[hsl(var(--glass-bg))] hover:backdrop-blur-sm hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-12 px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
